import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Home, User, LayoutGrid, MessageCircle, CalendarDays, Users,
  Briefcase, FolderOpen, FileText, BadgeDollarSign, Gift,
  Newspaper, ShoppingBag, GraduationCap, Share2, HelpCircle,
  Search, ChevronDown, Menu, X, Bell, ShieldCheck, LogOut, LayoutDashboard, Building2, Eye, Landmark,
  Contact, Calculator, DollarSign, Ticket, Megaphone, Receipt, UserPlus,
  CheckSquare, Radio, Package, StickyNote,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import AccountSwitcher from "@/components/AccountSwitcher";
import OnlineNowBubble from "@/components/OnlineNowBubble";
import "@/components/sidenav.css";

const LOGO = "/brand/wordmark-color.png";

const GROUPS = [
  { key: "your-space", heading: "Your space", color: "coral", items: [
    { key: "home", label: "Home", to: "/", icon: Home, end: true },
    { key: "me", label: "My 2°", to: "/me", icon: User },
  ] },
  { key: "connect", heading: "Connect", color: "sky", items: [
    { key: "community", label: "Community", to: "/community", icon: LayoutGrid },
    { key: "groups", label: "Groups", to: "/groups", icon: Users },
    { key: "messages", label: "Messages", to: "/messages", icon: MessageCircle, badge: true },
    { key: "events", label: "Events", to: "/events", icon: CalendarDays },
    { key: "members", label: "Members", to: "/members", icon: Users },
  ] },
  { key: "get-things-done", heading: "Get things done", color: "sage", items: [
    { key: "jobs", label: "Opportunities", to: "/jobs", icon: Briefcase },
    { key: "projects", label: "Projects", to: "/projects", icon: FolderOpen },
    { key: "docs", label: "Docs", to: "/docs", icon: FileText },
    { key: "notes", label: "Notes", to: "/notes", icon: StickyNote },
    { key: "myhr", label: "My HR", to: "/my-hr", icon: BadgeDollarSign },
  ] },
  { key: "perks", heading: "Perks", color: "amber", items: [
    { key: "rewards", label: "Rewards", to: "/rewards", icon: Gift },
  ] },
];

// --- Business member-facing navigation (shown when the account manages a business) ---
// Static groups; the dynamic "Grow my business" + "Business tools" items are appended from
// /business/my-tools so a teammate only sees the tools their business admin granted them.
const BIZ_STATIC_GROUPS = [
  { key: "biz-home", heading: "Home", color: "coral", items: [
    { key: "biz-dashboard", label: "Business Dashboard", to: "/business/console", icon: LayoutDashboard },
    { key: "checkin", label: "Daily Check-In", to: "/", icon: CheckSquare, end: true },
    { key: "me", label: "My 2°", to: "/me", icon: User },
    { key: "notes", label: "Notes", to: "/notes", icon: StickyNote },
  ] },
  { key: "biz-network", heading: "Network", color: "sky", items: [
    { key: "connections", label: "Connections", to: "/connections", icon: Users },
    { key: "groups", label: "Groups", to: "/groups", icon: Users },
    { key: "team-chat", label: "Team Chat", to: "/team-chat", icon: MessageCircle, badge: true },
    { key: "messages", label: "Messages", to: "/messages", icon: MessageCircle, badge: true },
    { key: "members", label: "Members", to: "/members", icon: Users },
  ] },
  { key: "biz-community", heading: "Community", color: "sky", items: [
    { key: "community", label: "Community Posts", to: "/community", icon: LayoutGrid },
    { key: "press", label: "The Press", to: "/press", icon: Newspaper },
    { key: "live", label: "Live Sessions", to: "/learn/live", icon: Radio },
  ] },
  { key: "biz-events", heading: "Events & Calendar", color: "sage", items: [
    { key: "events", label: "Events", to: "/events", icon: CalendarDays },
    { key: "tickets", label: "Tickets", to: "/tickets", icon: Ticket },
    { key: "calendar", label: "Scheduling & Calendar", to: "/calendar", icon: CalendarDays },
  ] },
  { key: "biz-opportunities", heading: "Opportunities", color: "sage", items: [
    { key: "jobs", label: "Opportunities", to: "/jobs", icon: Briefcase },
    { key: "learning", label: "Learning", to: "/learn", icon: GraduationCap },
  ] },
];

const BIZ_ACCOUNT_GROUP = { key: "biz-account", heading: "Account", color: "amber", items: [
  { key: "wallet", label: "Wallet & Payments", to: "/wallet", icon: DollarSign },
  { key: "rewards", label: "Rewards", to: "/rewards", icon: Gift },
  { key: "inventory", label: "Inventory", to: "/business/console?tab=pos", icon: Package },
] };

// tool key -> { label fallback, route, icon } for the dynamic Grow + Business Tools groups.
const TOOL_META = {
  crm: { to: "/business/console?tab=crm", icon: Contact },
  social: { to: "/social", icon: Share2 },
  "email-marketing": { to: "/business/console?tab=marketing", icon: Megaphone },
  projects: { to: "/projects", icon: FolderOpen },
  docs: { to: "/docs", icon: FileText },
  contracts: { to: "/business/console?tab=contracts", icon: FileText },
  team: { to: "/business/console?tab=team", icon: Users },
  hr: { to: "/business/console?tab=hr", icon: UserPlus },
  payroll: { to: "/business/console?tab=payroll", icon: BadgeDollarSign },
  recruiting: { to: "/business/console?tab=recruiting", icon: Briefcase },
  "business-finance": { to: "/business/console?tab=payments", icon: DollarSign },
  accounting: { to: "/business/console?tab=accounting", icon: Calculator },
  expenses: { to: "/expenses", icon: Receipt },
};

function buildBizGroups(tools) {
  const toItem = (t) => ({ key: `tool-${t.key}`, label: t.label, to: (TOOL_META[t.key] || {}).to || "/business/console", icon: (TOOL_META[t.key] || {}).icon || FileText });
  const grow = (tools || []).filter((t) => t.group === "grow").map(toItem);
  const biz = (tools || []).filter((t) => t.group === "tools").map(toItem);
  const groups = [...BIZ_STATIC_GROUPS];
  if (grow.length) groups.push({ key: "biz-grow", heading: "Grow My Business", color: "plum", items: grow });
  if (biz.length) groups.push({ key: "biz-tools", heading: "Business Tools", color: "sage", items: biz });
  groups.push(BIZ_ACCOUNT_GROUP);
  return groups;
}

// Member sidebar items gated by a feature module. An item appears only when the
// module is ACTIVE for the member's tier (features[mod] === true); coming-soon modules
// surface in the "Coming soon" section instead, everything else is hidden entirely.
// NOTE: Home, My 2°, Members and Messages (direct messages) are core and never gated.
// "My HR" is special-cased to the employee relationship (user.hr_linked), not a tier flag.
const NAV_MODULE = {
  community: "community",
  groups: "groups",
  events: "events",
  jobs: "jobs",
  projects: "projects",
  docs: "docs",
  rewards: "rewards",
};

// Display metadata for coming-soon modules that have a sidebar representation.
// The "Coming soon" section is built from the member's actual coming_soon list (per tier).
const SOON_META = {
  press: { label: "The Press", icon: Newspaper },
  calendar: { label: "Calendar", icon: CalendarDays },
  store: { label: "Store", icon: ShoppingBag },
  learning: { label: "Learning", icon: GraduationCap },
  social: { label: "Social", icon: Share2 },
  live: { label: "Live Sessions", icon: Radio },
  jobs: { label: "Opportunities", icon: Briefcase },
  projects: { label: "Projects", icon: FolderOpen },
  docs: { label: "Docs", icon: FileText },
  rewards: { label: "Rewards", icon: Gift },
  community: { label: "Community", icon: LayoutGrid },
  events: { label: "Events", icon: CalendarDays },
  groups: { label: "Groups", icon: Users },
};

const groupVar = (c) => ({ "--group": `var(--${c})` });

function Item({ item, color, count = 0, onNavigate }) {
  const showBadge = item.badge && count > 0;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={`nav-${item.key}`}
      onClick={onNavigate}
      style={groupVar(color)}
      className={({ isActive }) => "sn-item" + (isActive ? " sn-item--active" : "")}
    >
      <span className="sn-bubble"><item.icon strokeWidth={2} /></span>
      <span className="sn-label">{item.label}</span>
      {showBadge && (
        <span className="sn-badge" data-testid={`nav-${item.key}-badge`}>
          {count > 99 ? "99+" : count}
        </span>
      )}
    </NavLink>
  );
}

// Which unread counter drives a nav item's badge.
function itemBadgeCount(key, { msgUnread, teamChatUnread }) {
  if (key === "messages") return msgUnread;
  if (key === "team-chat") return teamChatUnread;
  return 0;
}

function NavBody({ points, msgUnread, teamChatUnread, notifUnread, pending, isAdmin, tdBusiness, managesBusiness, isBusiness, canPreview, previewMember, onTogglePreview, onNavigate, onLogout, nav }) {
  const [q, setQ] = useState("");
  const { features, comingSoon, user } = useAuth();
  const [soonOpen, setSoonOpen] = useState(false);
  const [soonFx, setSoonFx] = useState({});
  const timers = useRef({});

  const ql = q.trim().toLowerCase();
  const match = useCallback((label) => !ql || label.toLowerCase().includes(ql), [ql]);

  const { data: bizTools } = useQuery({
    queryKey: ["my-business-tools"],
    queryFn: () => api("/business/my-tools"),
    enabled: !!managesBusiness,
    staleTime: 60_000,
  });
  const baseGroups = useMemo(
    () => (managesBusiness ? buildBizGroups(bizTools?.tools) : GROUPS),
    [managesBusiness, bizTools]
  );

  // Tier-aware visibility for the member sidebar: a module-gated item shows only when
  // active for the member's tier; "My HR" only for active employees (hr_linked); core
  // items are always shown. The business sidebar is tool-driven and is left untouched.
  const gate = useCallback((it) => {
    if (!match(it.label)) return false;
    if (managesBusiness) return true;
    if (it.key === "myhr") return !!user?.hr_linked;
    const mod = NAV_MODULE[it.key];
    if (!mod) return true;
    return !!features && features[mod] === true;
  }, [match, managesBusiness, user, features]);

  const visibleGroups = useMemo(
    () => baseGroups.map((g) => ({ ...g, items: g.items.filter(gate) })).filter((g) => g.items.length),
    [baseGroups, gate]
  );
  const soonMatches = useMemo(
    () => (managesBusiness ? [] : (comingSoon || [])
      .filter((k) => SOON_META[k] && match(SOON_META[k].label))
      .map((k) => ({ key: k, ...SOON_META[k] }))),
    [comingSoon, match, managesBusiness]
  );

  // Dev aid: warn if the backend marks a module "coming soon" that has no sidebar
  // metadata, so a newly-added tier feature can't silently drop out of the menu.
  useEffect(() => {
    if (managesBusiness || !comingSoon?.length) return;
    const missing = comingSoon.filter((k) => !SOON_META[k]);
    if (missing.length) console.warn(`[SideNav] Coming-soon modules without sidebar metadata (not shown in menu): ${missing.join(", ")}`);
  }, [comingSoon, managesBusiness]);
  const helpMatch = match("Help");
  const total = visibleGroups.reduce((n, g) => n + g.items.length, 0) + soonMatches.length + (helpMatch ? 1 : 0);
  const soonForceOpen = !!ql && soonMatches.length > 0;
  const soonShow = soonOpen || soonForceOpen;

  useEffect(() => () => { Object.values(timers.current).forEach(clearTimeout); }, []);

  const clickSoon = (key) => {
    setSoonFx((p) => ({ ...p, [key]: { shake: true, ready: true } }));
    clearTimeout(timers.current[`${key}-s`]);
    clearTimeout(timers.current[`${key}-r`]);
    timers.current[`${key}-s`] = setTimeout(() => setSoonFx((p) => ({ ...p, [key]: { ...(p[key] || {}), shake: false } })), 400);
    timers.current[`${key}-r`] = setTimeout(() => setSoonFx((p) => ({ ...p, [key]: { shake: false, ready: false } })), 1400);
  };

  return (
    <>
      <div className="sn-logo-wrap">
        <button
          type="button"
          className="sn-logo-btn"
          data-testid="nav-logo"
          aria-label="Go to home"
          onClick={() => { nav("/"); onNavigate?.(); }}
        >
          <img src={LOGO} alt="Two Degrees" className="sn-logo" />
        </button>
      </div>

      <div className="sn-search-wrap">
        <Search className="sn-search-icon" size={18} strokeWidth={2} />
        <input
          type="text"
          className="sn-search"
          data-testid="nav-search-input"
          placeholder="Search the menu"
          aria-label="Search the menu"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <button type="button" className="sn-points" data-testid="points-pill" onClick={() => nav("/rewards")}>
        <span className="sn-points-gift"><Gift size={18} strokeWidth={2} /></span>
        <span className="sn-points-label">{points} points</span>
        <span className="sn-points-spend">Spend</span>
      </button>

      <div style={{ padding: "8px 2px 2px" }}>
        <OnlineNowBubble className="w-full justify-center" />
      </div>

      {total === 0 ? (
        <p className="sn-empty" data-testid="nav-no-matches">No matches. Try another word.</p>
      ) : (
        <>
          {visibleGroups.map((g) => (
            <div key={g.key} data-testid={`nav-group-${g.key}`}>
              <div className="sn-heading" style={groupVar(g.color)}>{g.heading}</div>
              <nav aria-label={g.heading}>
                {g.items.map((it) => (
                  <Item key={it.key} item={it} color={g.color} count={itemBadgeCount(it.key, { msgUnread, teamChatUnread })} onNavigate={onNavigate} />
                ))}
              </nav>
            </div>
          ))}

          {soonMatches.length > 0 && (
            <div style={groupVar("plum")}>
              <button
                type="button"
                className="sn-soon-toggle"
                data-testid="coming-soon-toggle"
                aria-expanded={soonShow}
                aria-controls="sn-coming-soon"
                onClick={() => setSoonOpen((v) => !v)}
              >
                Coming soon
                <span className="sn-soon-count">{soonMatches.length}</span>
                <ChevronDown className={"sn-soon-chevron" + (soonShow ? " open" : "")} size={18} strokeWidth={2} />
              </button>
              {soonShow && (
                <div id="sn-coming-soon">
                  {soonMatches.map((it) => {
                    const fx = soonFx[it.key] || {};
                    return (
                      <button
                        key={it.key}
                        type="button"
                        data-testid={`nav-soon-${it.key}`}
                        aria-disabled="true"
                        style={groupVar("plum")}
                        className={"sn-item sn-soon-item" + (fx.shake ? " sn-shake" : "")}
                        onClick={() => clickSoon(it.key)}
                      >
                        <span className="sn-bubble"><it.icon strokeWidth={2} /></span>
                        <span className="sn-label">{it.label}</span>
                        <span className={"sn-soon-tag" + (fx.ready ? " ready" : "")} data-testid={`nav-soon-${it.key}-tag`}>
                          {fx.ready ? "Almost ready!" : "Soon"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div className="sn-grow" />

      {helpMatch && (
        <>
          <hr className="sn-divider-dashed" />
          <nav aria-label="Help">
            <Item item={{ key: "help", label: "Help", to: "/help", icon: HelpCircle }} color="coral" count={0} onNavigate={onNavigate} />
          </nav>
        </>
      )}

      <div className="sn-footer">
        {managesBusiness && (
          <NavLink to="/business/console" data-testid="nav-business-console" className="sn-util" onClick={onNavigate}>
            <LayoutDashboard strokeWidth={2} />
            Business Dashboard
          </NavLink>
        )}
        {isBusiness && (
          <NavLink to="/business" data-testid="nav-host" className="sn-util" onClick={onNavigate}>
            <Building2 strokeWidth={2} />
            Host an event
          </NavLink>
        )}
        <NavLink to="/notifications" data-testid="nav-notifications" className="sn-util" onClick={onNavigate}>
          <Bell strokeWidth={2} />
          Notifications
          {notifUnread > 0 && <span className="sn-badge" data-testid="nav-notifications-badge">{notifUnread > 99 ? "99+" : notifUnread}</span>}
        </NavLink>
        {tdBusiness && (
          <NavLink to="/two-degrees" data-testid="nav-two-degrees" className="sn-util" onClick={onNavigate}>
            <Landmark strokeWidth={2} />
            Two Degrees LLC
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/admin" data-testid="nav-admin" className="sn-util" onClick={onNavigate}>
            <ShieldCheck strokeWidth={2} />
            Admin Dashboard
            {pending > 0 && <span className="sn-badge" data-testid="nav-admin-badge">{pending}</span>}
          </NavLink>
        )}
        {canPreview && (
          <button type="button" className="sn-util" data-testid="nav-preview-member" onClick={() => { onTogglePreview?.(); onNavigate?.(); }}>
            <Eye strokeWidth={2} />
            {previewMember ? "Exit member preview" : "Preview as member"}
          </button>
        )}
        <div style={{ padding: "6px 4px" }}><AccountSwitcher /></div>
        <button type="button" className="sn-util" data-testid="nav-logout" onClick={onLogout}>
          <LogOut strokeWidth={2} />
          Log out
        </button>
      </div>
    </>
  );
}

export default function SideNav({ points = 0, msgUnread = 0, teamChatUnread = 0, notifUnread = 0, pending = 0, managesBusiness = false, isBusiness = false, canPreview = false, previewMember = false, onTogglePreview, onLogout }) {
  const { isAdmin, user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const hamburgerRef = useRef(null);
  const drawerRef = useRef(null);
  const touchX = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  // body scroll lock + Esc + focus management while the drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); return; }
      if (e.key === "Tab" && drawerRef.current) {
        const f = drawerRef.current.querySelectorAll('a[href],button:not([disabled]),input,[tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => {
      const f = drawerRef.current?.querySelector('a[href],button:not([disabled]),input');
      f?.focus();
    }, 60);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      hamburgerRef.current?.focus();
    };
  }, [open]);

  const bodyProps = { points, msgUnread, teamChatUnread, notifUnread, pending, isAdmin, tdBusiness: !previewMember && !!user?.td_business_access, managesBusiness, isBusiness, canPreview, previewMember, onTogglePreview, onLogout, nav };

  return (
    <>
      {/* Desktop fixed panel */}
      <aside
        className="sidenav-root sn-panel hidden lg:flex lg:flex-col"
        style={{ position: "fixed", top: 16, left: 16, bottom: 16, overflowY: "auto" }}
        aria-label="Main menu"
        data-testid="sidenav-desktop"
      >
        <NavBody {...bodyProps} onNavigate={undefined} />
      </aside>

      {/* Mobile top bar */}
      <header className="sidenav-root sn-topbar lg:hidden" data-testid="mobile-topbar">
        <button
          ref={hamburgerRef}
          type="button"
          className="sn-hamburger"
          data-testid="mobile-menu-button"
          aria-label="Open menu"
          aria-expanded={open}
          aria-controls="sn-drawer"
          onClick={() => setOpen(true)}
        >
          <Menu strokeWidth={2} />
        </button>
        <button type="button" className="sn-topbar-logo-btn" data-testid="mobile-topbar-logo" aria-label="Go to home" onClick={() => nav("/")}>
          <img src={LOGO} alt="Two Degrees" className="sn-topbar-logo" />
        </button>
        <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}>
          <OnlineNowBubble />
        </span>
      </header>

      {/* Mobile drawer + scrim */}
      {open && (
        <div
          className="sn-scrim lg:hidden"
          data-testid="mobile-scrim"
          onClick={close}
          aria-hidden="true"
        />
      )}
      <aside
        id="sn-drawer"
        ref={drawerRef}
        className={"sidenav-root sn-drawer lg:hidden" + (open ? " open" : "")}
        aria-label="Main menu"
        aria-hidden={!open}
        data-testid="sidenav-drawer"
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchMove={(e) => {
          if (touchX.current == null) return;
          if (e.touches[0].clientX - touchX.current < -50) { touchX.current = null; close(); }
        }}
        onTouchEnd={() => { touchX.current = null; }}
      >
        <NavBody {...bodyProps} onNavigate={close} />
      </aside>
    </>
  );
}
